import { AB_KEYS, getVariant } from '@/lib/abExperiments';
import { getBasket } from '@/lib/BasketStorage';
import { hashSHA256 } from '@/lib/crypto/SHA256';
import { getBasketEcommerceBody } from '@/lib/GTMEventType';
import { sendCRODataLayerEvent, sendGTMEvent } from '@/lib/gtm-wrapper';
import { getIsAnonymous, getLocalUser } from '@/lib/LocalStorage';
import { User } from '@/models/User';

export function pushCheckoutEcommerce(event: string, extra?: Record<string, unknown>) {
  const basket = getBasket();
  if (!basket?.items?.length) return;
  sendGTMEvent({
    event,
    ecommerce: {
      ...getBasketEcommerceBody(basket),
      ...extra,
    },
  });
}

export function pushMicroFunnel(action: string, label: string) {
  sendCRODataLayerEvent({ ecommerce: null });
  sendCRODataLayerEvent({
    event: 'micro_funnel',
    action,
    label,
  });
}

/** Same-tick pushes collapse to a single GTM/GA hit. Space them so each field is its own event. */
const MICRO_FUNNEL_GAP_MS = 120;

export function pushMicroFunnels(items: { action: string; label: string }[]) {
  items.forEach((item, index) => {
    if (index === 0) {
      pushMicroFunnel(item.action, item.label);
      return;
    }
    window.setTimeout(() => {
      pushMicroFunnel(item.action, item.label);
    }, index * MICRO_FUNNEL_GAP_MS);
  });
}

export type CheckoutAbGroup = 'control' | 'v1';

const CHECKOUT_AB_TEST_NAME = 'Test No 1 - Checkout';

export function checkoutAbTestVariation(group: CheckoutAbGroup): string {
  return group === 'v1' ? `${CHECKOUT_AB_TEST_NAME} - V1` : `${CHECKOUT_AB_TEST_NAME} - Control`;
}

/** A / mevcut checkout = Control, B / yeni checkout = V1. Group is the screen, not the cookie. */
export function pushCheckoutAbTest(group: CheckoutAbGroup) {
  sendCRODataLayerEvent({ ecommerce: null });
  sendCRODataLayerEvent({
    event: 'gtm_ab_test',
    gtm_ab_test_name: CHECKOUT_AB_TEST_NAME,
    gtm_ab_test_variation: checkoutAbTestVariation(group),
  });
}

export function userPageFields(source?: User): Record<string, unknown> {
  const user = source ?? getLocalUser();
  const loggedIn = source
    ? Number(source.isAnonymous) !== 1
    : Boolean(user && !getIsAnonymous() && Number(user.isAnonymous) !== 1);
  const phone = String(user?.msisdn || '').replace(/\D/g, '');
  const phone90 = phone ? (phone.startsWith('90') ? phone : `90${phone.slice(-10)}`) : '';
  const parts = String(user?.title || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const payload: Record<string, unknown> = {
    login_status: loggedIn ? 'Login' : 'Guest',
    ab_checkout: (getVariant(AB_KEYS.checkout) || 'a').toUpperCase(),
  };
  if (loggedIn && user) {
    payload.user_id = user.id;
    if (user.birthdate) payload.birthday = user.birthdate;
    if (user.email) payload.user_mail = hashSHA256(user.email.trim().toLowerCase());
    if (phone90) {
      payload.user_phone = hashSHA256(phone90);
      payload.user_phone_plus = hashSHA256(`+${phone90}`);
    }
    if (parts[0]) payload.firstname = parts[0];
    if (parts.length > 1) payload.lastname = parts.slice(1).join(' ');
  }
  return payload;
}
